import React, { useMemo } from 'react';
import { Alert, Input, Spin, Select } from "antd";
import DatePicker from '../DatePickerAutoAccept/DatePickerAutoAccept';
import { useForm, FormProvider } from 'react-hook-form';
import { useDispatch, useSelector } from "react-redux";
import * as actionCreators from "../../store/actions/index";
import Enums from '../../shared/enums';
import moment from 'moment';
import Document from '../Document/Document';
import FormItem from '../FormItem/FormItem';
import Form from '../Form/Form';
import FormButtons from '../FormButtons/FormButtons';
import { isNotNullOrUndefined, isObjectNotEmpty } from '../../shared/objectUtils';
import { CanSee } from '../../shared/entitlements/entitlements';

const EditDocument = ({ cancel, document }) => {
    const methods = useForm({ mode: 'all', reValidateMode: 'onChange', criteriaMode: 'all', shouldFocusError: true, shouldUnregister: true });

    const dispatch = useDispatch();
    const isLoading = useSelector(state => state.documents.isRecordUpdateLoading);
    const error = useSelector(state => state.documents.updateRecordError);

    const documentVisibilityOptions = Enums.DocumentVisibilities.selectListOptions();

    const onSubmit = (data) => {
        data.entityId = document.entityId;
        data.entityType = document.entityType;

        let payload = {};
        let objectToUpdate = { ...document };
        for (let [key, value] of Object.entries(data)) {
            let oldValue = objectToUpdate[key] !== undefined && objectToUpdate[key] !== null ? objectToUpdate[key] : '';
            if (value !== oldValue) {
                //console.log(`${key}: new value - ${value}, old value - ${oldValue}`);
                payload[key] = value;
            }
        }

        if (isObjectNotEmpty(payload)) {
            dispatch(actionCreators.updateDocument(document.id, payload));
        } else {
            onCancel();
        }
    };

    const onCancel = () => {
        dispatch(actionCreators.updateDocumentCancel());
        cancel();
    };

    useMemo(() => {
        // clear any previous errors if this is a new form
        dispatch(actionCreators.updateDocumentErrorClear());
    }, []);

    useMemo(() => {
        if (isLoading !== null && isLoading === false && error === null) {
            onCancel();
        }
    }, [isLoading, error]);

    const formItemLayout = {
        labelCol: { span: 8 },
        wrapperCol: { span: 16 },
    };

    return (
        <FormProvider {...methods}>
            <Form onSubmit={methods.handleSubmit(onSubmit)}>
                <Spin style={{ height: '100%', width: '100%' }} size="large" spinning={isLoading === true && error === null}>
                    <FormItem {...formItemLayout} label="File Name" required
                        render={({ onChange, onBlur, value, name, ref }) => <Input onBlur={onBlur} onChange={e => { onChange(e.target.value); }} value={value} name={name} placeholder="File Name" ref={ref} />}
                        rules={{ required: 'Required Field' }}
                        name="displayName"
                        defaultValue={document.displayName}
                    />
                    <FormItem {...formItemLayout} label="File Description"
                        render={({ onChange, onBlur, value, name, ref }) => <Input onBlur={onBlur} onChange={e => { onChange(e.target.value); }} value={value} name={name} placeholder="File Description" ref={ref} />}
                        name="description"
                        defaultValue={document.description}
                    />
                    {isObjectNotEmpty(document) && document.entityType === "DRIVER" ? (
                        <FormItem {...formItemLayout} label="License Number"
                            render={({ onChange, onBlur, value, name, ref }) => <Input onBlur={onBlur} onChange={e => { onChange(e.target.value); }} value={value} name={name} placeholder="License Number" ref={ref} />}
                            name="licenseNumber"
                            defaultValue={document.licenseNumber}
                        />
                    ) : null}
                    {isObjectNotEmpty(document) && (document.entityType === "DRIVER" || document.documentType === "ACCOUNT_CARRIER" || isNotNullOrUndefined(document.expiryDate)) ? (
                        <FormItem {...formItemLayout} label="Expiration Date"
                            render={({ onChange, onBlur, value, name, ref }) => (
                                <DatePicker
                                    placeholder="Expiration Date"
                                    style={{ width: '100%' }}
                                    onBlur={onBlur}
                                    onChange={(date, dateString) => { onChange(date); }}
                                    value={value}
                                    name={name}
                                    onSelect={(date) => { onChange(date); }}
                                    ref={ref}
                                />
                            )}
                            name="expiryDate"
                            defaultValue={document.expiryDate ? moment(document.expiryDate) : null}
                        />
                    ) : null}
                    <CanSee staffOnly={true}>
                        <FormItem {...formItemLayout} label="Visibility" required
                            render={({ onChange, onBlur, value, name, ref }) => (
                                <Select
                                    placeholder="Please select the Visibility"
                                    allowClear={true}
                                    style={{ width: '100%' }}
                                    virtual={false}
                                    onBlur={onBlur}
                                    onChange={(selected) => { onChange(selected); }}
                                    value={value}
                                    name={name}
                                    ref={ref}
                                >
                                    {documentVisibilityOptions}
                                </Select>
                            )}
                            rules={{ required: 'Required Field' }}
                            name="visibleTo"
                            defaultValue={document.visibleTo}
                        />
                    </CanSee>
                    {error && <Alert message={`${error}`} type="error" />}
                    <Document fileId={document.id} />
                </Spin>
                <FormButtons cancel={onCancel} disabled={isLoading === true && error === null} submitText="Update Document" />
            </Form>
        </FormProvider>
    );
};

export default EditDocument;